import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Show } from '../../show';

import AssignmentTimeWarning from '../../assignment-time-warning';
import CheckBox from '../../check-box-group/check-box';
import FluidContainer from '../../layouts/components/fluid-container';
import Skeleton from '../../skeleton';
import Spacer from '../../spacer';
import { Body, Heading, Helper } from '../../typography';
import { textAssignmentEndDate } from '../../../lib/utils';
import { SOURCES } from '../../../constants/sources';

export const textPause = (pausable, isGeneratedHomework) => {
    if (pausable) {
        return 'puoi mettere la prova in pausa e riprenderla quando vuoi;';
    }
    return `non puoi mettere la prova in pausa.${
        isGeneratedHomework ? '' : ' Una volta iniziato, il tempo continuerà a scorrere anche se chiudi la finestra;'
    }`;
};

export const textRepeats = (maxAttemptsNumber, attemptsNumber) => {
    if (maxAttemptsNumber <= 0) {
        return 'puoi svolgere la prova tutte le volte che vuoi.';
    }

    const remainingAttemptsNumber = maxAttemptsNumber - attemptsNumber;

    if (remainingAttemptsNumber === 1) {
        return 'hai a disposizione 1 tentativo: puoi usarlo anche in un altro momento.';
    }

    return `hai a disposizione ${remainingAttemptsNumber} tentativi: puoi usarli anche in un altro momento.`;
};

export const textPoints = (points) => {
    if (points === 1 || points === 1.0) {
        return '1 punto';
    }
    return `${points?.toString().replace('-', '−')} punti`;
};

const skeletonItems = [
    { width: 40 },
    { width: 0, height: 'small' },
    { width: 100 },
    { width: 100 },
    { width: 60 },
    { width: 0 },
    { width: 40, height: 'regular' },
    { width: 100, height: 'regular' },
];

const DefaultStartPage = ({
    assignmentEndDate,
    assignmentId,
    attemptsNumber,
    correctable,
    expiringAssignment,
    getStatusArray,
    isChecked,
    isGeneratedHomework,
    isLoading,
    maxAttemptsNumber,
    onChange,
    pausable,
    remainingTime,
    scoreModifier,
    source,
    time,
}) => {
    useEffect(() => {
        if (assignmentId && maxAttemptsNumber > 0 && source !== SOURCES.LAZE) {
            getStatusArray(assignmentId);
        }
    }, [assignmentId, getStatusArray, maxAttemptsNumber, source]);

    return (
        <FluidContainer flexDirection="column" maxWidth={['100%', '50%', '50%']} paddingTop={[178, 168, 140]}>
            <Skeleton isLoading={isLoading} items={skeletonItems}>
                <Body lv={4} weight="semi-bold">
                    {'PRIMA DI INIZIARE'}
                </Body>

                <Spacer size="small" />

                <Body>
                    <Show when={assignmentEndDate}>
                        <strong>{textAssignmentEndDate(assignmentEndDate)}</strong>
                        {expiringAssignment && <AssignmentTimeWarning time={time} remainingTime={remainingTime} />}
                        <Spacer size="base" />
                    </Show>

                    {'Stai per cominciare una prova. Ricorda:'}
                    <ul>
                        <Show when={time > 0}>
                            <li>{'questa prova è a tempo. Il tempo a disposizione è indicato in alto a destra;'}</li>
                        </Show>
                        <Show when={time > 0}>
                            <li>{'il conto alla rovescia parte appena inizi la prova;'}</li>
                        </Show>
                        <li>{textPause(pausable, isGeneratedHomework)}</li>
                        <Show when={correctable}>
                            <li>{'puoi controllare subito di aver risposto bene alle domande con il pulsante "Correggi".'}</li>
                        </Show>
                        <li>{textRepeats(maxAttemptsNumber, attemptsNumber)}</li>
                    </ul>

                    <Spacer size="small" />

                    <Show when={source === SOURCES.TT}>
                        <Body>
                            <strong>{'Come si calcola il punteggio?'}</strong>
                            <Spacer size="base" />
                            {'Una risposta saltata viene esclusa dal voto.'}
                            {'Una risposta incompleta conta come un errore.'}
                        </Body>
                    </Show>

                    <Show when={source === SOURCES.ZTE_UNITUTOR && !!scoreModifier}>
                        {`Un esercizio giusto vale +${textPoints(scoreModifier?.correct)}, uno sbagliato vale ${textPoints(
                            scoreModifier?.wrong,
                        )}.`}
                    </Show>

                    <Show when={source !== SOURCES.TT && source !== SOURCES.ZTE_UNITUTOR}>
                        <Body>
                            <strong>{'Come si calcola il punteggio?'}</strong>
                            <Spacer size="base" />
                            {`Ogni prova ha un punteggio totale: il tuo
                             punteggio sarà la somma dei risultati ottenuti nei
                              punteggi parziali dei singoli esercizi.
                              Il voto si calcola a partire dal punteggio che hai ottenuto.`}
                            <Spacer size="base" />
                            {'Ricorda:'}
                            <ul>
                                <li>{'gli esercizi Risposta Aperta sono esclusi dalla somma;'}</li>
                                <li>{'gli esercizi non svolti valgono 0 punti.'}</li>
                            </ul>
                        </Body>
                    </Show>

                    <Show when={!time > 0}>
                        {/** dovrebbero essere 24, 24, 48, ma la checkbox ha un margin interno */}
                        <FluidContainer marginTop={[0, 0, 24]}>
                            <CheckBox checked={isChecked} onChange={onChange} value={0}>
                                {'Ok, non mostrarmi più il messaggio'}
                            </CheckBox>
                        </FluidContainer>
                    </Show>
                </Body>

                <FluidContainer marginTop={[24, 24, 48]}>
                    <Heading level={2} weight="light">
                        {'Iniziamo?'}
                    </Heading>
                </FluidContainer>
                <Helper weight="semi-bold">{'Usa il pulsante "Inizia"'}</Helper>
            </Skeleton>
        </FluidContainer>
    );
};

DefaultStartPage.propTypes = {
    assignmentEndDate: PropTypes.string,
    assignmentId: PropTypes.string,
    attemptsNumber: PropTypes.number,
    correctable: PropTypes.bool,
    expiringAssignment: PropTypes.string,
    getStatusArray: PropTypes.func,
    isChecked: PropTypes.bool,
    isGeneratedHomework: PropTypes.bool,
    isLoading: PropTypes.bool,
    maxAttemptsNumber: PropTypes.number,
    onChange: PropTypes.func,
    pausable: PropTypes.bool,
    remainingTime: PropTypes.number,
    scoreModifier: PropTypes.object,
    source: PropTypes.string,
    time: PropTypes.number,
};

export default DefaultStartPage;
